import { useMemo } from 'react';

import { ITicket } from '@entities/tickets/model';
import { client } from '@shared/api';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

export enum ECardStatus {
  active = 'active',
  inactive = 'inactive',
  frozen = 'frozen',
}

export interface ICard {
  id: string;
  pin: string | null;
  cvv: string;
  number: string;
  expiresAt: string;
  name: string;
  holder: string;
  status: ECardStatus;
  lastFour: string;
}

export interface ICardUnmasked {
  response: string;
}

export interface IPaginatedCards {
  results: ICard[];
  pendingTickets?: ITicket[];
  total: number;
}

export interface IUpdateCardPayload {
  name: string;
}

export interface IGetCardsParams {
  sort?: 'asc' | 'desc';
  skip?: number;
  take?: number;
}

export const useGetCardUnmasked = (
  cardId: string,
  options?: Omit<UseQueryOptions<ICard, AxiosError>, 'queryKey' | 'queryFn'>,
) => {
  const query = useQuery<ICard, AxiosError>({
    queryKey: ['cardUnmasked', cardId],
    queryFn: async () => {
      const response = await client.get<ICardUnmasked>(
        `/client/cards/${cardId}/unmasked`,
      );
      const decodedData = JSON.parse(atob(response.data.response)) as ICard;
      return {
        ...decodedData,
        id: cardId,
      };
    },
    ...options,
  });

  return {
    unmaskedCard: query.data,
    ...query,
  };
};

export const useGetCard = (
  cardId: string,
  options?: Omit<UseQueryOptions<ICard, AxiosError>, 'queryKey' | 'queryFn'>,
) => {
  const query = useQuery<ICard, AxiosError>({
    queryKey: ['card', cardId],
    queryFn: () =>
      client.get(`/client/cards/${cardId}`).then((res) => res.data),
    ...options,
  });

  return {
    card: query.data,
    ...query,
  };
};

export const useGetCards = (
  params: IGetCardsParams = {},
  options?: Omit<
    UseQueryOptions<IPaginatedCards, AxiosError>,
    'queryKey' | 'queryFn'
  >,
) => {
  const query = useQuery<IPaginatedCards, AxiosError>({
    queryKey: ['cards', params],
    queryFn: () =>
      client.get('/client/cards', { params }).then((res) => res.data),
    ...options,
  });

  const cards = useMemo(
    () => query.data?.results || [],
    [query.data],
  ) as ICard[];
  const total = useMemo(() => query.data?.total || 0, [query.data]);
  const pendingTickets = useMemo(
    () => query.data?.pendingTickets || [],
    [query.data],
  );

  return {
    cards,
    total,
    pendingTickets,
    ...query,
  };
};

export const useRequestCvv = (cardId: string) => {
  const query = useMutation<void, AxiosError>({
    mutationKey: ['request-card-cvv', cardId],
    mutationFn: () =>
      client
        .post(`/client/cards/${cardId}/send-cvv-to-email`)
        .then((res) => res.data),
  });

  return { ...query };
};

export const useFreezeCard = (
  cardId: string,
  options?: Omit<
    UseMutationOptions<void, AxiosError>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const mutation = useMutation<void, AxiosError>({
    mutationKey: ['freeze-card', cardId],
    mutationFn: () =>
      client.post(`/client/cards/${cardId}/freeze`).then((res) => res.data),
    ...options,
  });

  return { ...mutation };
};

export const useUnFreezeCard = (
  cardId: string,
  options?: Omit<
    UseMutationOptions<void, AxiosError>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const mutation = useMutation<void, AxiosError>({
    mutationKey: ['unfreeze-card', cardId],
    mutationFn: () =>
      client.post(`/client/cards/${cardId}/unfreeze`).then((res) => res.data),
    ...options,
  });

  return { ...mutation };
};

interface DeleteCardOptions {
  currentPassword: string;
  cardId: string;
}

export const useDeleteCard = (
  options?: Omit<
    UseMutationOptions<
      void,
      AxiosError<{ message: string }>,
      DeleteCardOptions
    >,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const mutation = useMutation<
    void,
    AxiosError<{ message: string }>,
    DeleteCardOptions
  >({
    mutationKey: ['delete-card'],
    mutationFn: ({ cardId, currentPassword }) =>
      client
        .post(`/client/cards/${cardId}/close`, { currentPassword })
        .then((res) => res.data),
    ...options,
  });

  return { ...mutation };
};

interface UpdatePinOptions {
  currentPassword: string;
  newPin: string;
  cardId: string;
}

export const useUpdatePin = (
  options?: Omit<
    UseMutationOptions<void, AxiosError, UpdatePinOptions>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const mutation = useMutation<void, AxiosError, UpdatePinOptions>({
    mutationKey: ['update-pin'],
    mutationFn: ({ cardId, ...data }) =>
      client
        .post(`/client/cards/${cardId}/change-pin`, data)
        .then((res) => res.data),
    ...options,
  });

  return { ...mutation };
};

interface GetPinOptions {
  currentPassword: string;
  cardId: string;
}

export const useGetPin = (
  options?: Omit<
    UseMutationOptions<{ pin: string }, AxiosError, GetPinOptions>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const mutation = useMutation<{ pin: string }, AxiosError, GetPinOptions>({
    mutationKey: ['get-pin'],
    mutationFn: ({ cardId, ...data }) =>
      client
        .post(`/client/cards/${cardId}/get-pin`, data)
        .then((res) => res.data),
    ...options,
  });

  return { ...mutation };
};
